import { motion } from "framer-motion";
import { MapPin, Clock, GraduationCap, ArrowUpRight } from "lucide-react";
import { ABROAD_PROGRAMS, TEST_PREP, EXTERNAL_LINKS } from "@/lib/site-data";

export default function Programs() {
  return (
    <section id="programs" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
      >
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.3em] text-primary font-medium">Study Abroad</span>
          <h2 className="font-display text-4xl md:text-6xl text-secondary mt-4 leading-[1.02]">
            Global degrees, curated for you.
          </h2>
        </div>
        <a
          href={EXTERNAL_LINKS.careerbanale}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="programs-explore"
          className="inline-flex items-center gap-2 text-secondary font-medium border-b-2 border-primary pb-1 hover:gap-3 transition-all whitespace-nowrap"
        >
          Browse all programs <ArrowUpRight size={18} className="text-primary" />
        </a>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {ABROAD_PROGRAMS.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: i * 0.06 }}
            data-testid={`abroad-program-${i}`}
            className="group rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 hover:border-primary/50 transition-[transform,border-color] duration-400 hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-primary border border-primary/40 rounded-full px-3 py-1">
                {p.level}
              </span>
              <span className="flex items-center gap-1 text-sm text-foreground/60">
                <MapPin size={14} className="text-primary" /> {p.country}
              </span>
            </div>
            <h3 className="font-display text-2xl text-secondary mt-5 leading-snug min-h-[3.5rem]">{p.name}</h3>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/60">
              <span className="flex items-center gap-1.5 text-sm text-foreground/70">
                <Clock size={14} /> {p.duration}
              </span>
              <span className="text-sm font-medium text-secondary">{p.fee}<span className="text-foreground/50 font-normal">/yr</span></span>
            </div>
          </motion.div>
        ))}

        {/* Test prep card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="rounded-2xl bg-secondary text-secondary-foreground p-6 flex flex-col justify-between"
          data-testid="test-prep-card"
        >
          <div>
            <GraduationCap className="text-primary mb-3" />
            <h3 className="font-display text-2xl text-white leading-snug">Test Prep, online & offline</h3>
            <p className="text-white/60 text-sm mt-2">Score higher with expert-led coaching.</p>
          </div>
          <div className="flex flex-wrap gap-2 mt-5">
            {TEST_PREP.map((t) => (
              <span key={t} className="text-xs rounded-md border border-white/15 bg-white/[0.04] px-3 py-1.5 text-white/85">{t}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
